import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ShopBottomNav from '../../components/ShopBottomNav';
import {
  expireDealsIfNeeded,
  autoPromoteScheduledDeals,
  updateDeal,
  consumeToken,
  consumeTokens,
  getTokenBalance,
  refreshTokenBalance,
  endOfTodayISO,
  STATUS,
} from '../../services/shopState';
import {
  getMyDeals,
  updateDeal as apiUpdateDeal,
  updateDealStatus,
  deleteDeal as apiDeleteDeal,
  withAreaInfo,
  isRemovedDeal,
} from '../../services/apiService';
import './ShopDealsPage.css';

const isDemoMode = () => sessionStorage.getItem('walkin_demo_mode') === 'true';

const formatNlDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('nl-NL', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '—';

const TAB_KEYS = {
  ACTIVE:    'actief',
  SCHEDULED: 'gepland',
  PAUSED:    'gepauzeerd',
  EXPIRED:   'verlopen', // covers both verlopen + verwijderd
};

// Strict owner filter: only deals belonging to the logged-in shop owner.
function loadMyDeals() {
  expireDealsIfNeeded();
  autoPromoteScheduledDeals();
  let all = [];
  try { all = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
  catch { all = []; }
  const shopName = sessionStorage.getItem('walkin_shop_name');
  if (!shopName) return [];
  return all.filter(
    (d) => d.shopName === shopName || d.createdBy === shopName
  );
}

function countdownText(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'binnenkort';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hours}u`;
  if (hours > 0) return `${hours}u ${mins}m`;
  return `${mins}m`;
}

export default function ShopDealsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);

  // Live mode fetches the retailer's deals from the backend; demo mode (and
  // any API failure) falls back to the locally seeded deals.
  const refresh = useCallback(async () => {
    if (isDemoMode()) { setDeals(loadMyDeals()); return; }
    refreshTokenBalance(); // wallet from the backend, for the reactivate check
    // Removed deals (DELETE /deals/:id) are gone for the retailer: they are
    // kept in the database for statistics only.
    try { setDeals(await withAreaInfo((await getMyDeals()).filter((d) => !isRemovedDeal(d)))); }
    catch { setDeals(loadMyDeals()); }
  }, []);
  // Initial tab: read ?tab=actief|gepland|gepauzeerd|verlopen
  const initialTab = (() => {
    const t = searchParams.get('tab');
    if (t === TAB_KEYS.SCHEDULED || t === TAB_KEYS.PAUSED || t === TAB_KEYS.EXPIRED) return t;
    return TAB_KEYS.ACTIVE;
  })();
  const [tab, setTab] = useState(initialTab);
  const [modal, setModal] = useState(null); // 'no-tokens' | null
  const [toast, setToast] = useState('');
  const [confirmPermDelete, setConfirmPermDelete] = useState(null); // deal | null

  // Delete toast from edit page redirect
  useEffect(() => {
    if (searchParams.get('deleted') === '1') {
      setToast('🗑️ Deal verwijderd');
      const next = new URLSearchParams(searchParams);
      next.delete('deleted');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    refresh();
    const onStorage = (e) => {
      if (!e || !e.key || e.key === 'walkin_shop_deals') refresh();
    };
    window.addEventListener('walkin-deals-change', refresh);
    window.addEventListener('walkin-token-change', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', onStorage);
    // Bug 4/5: re-check every minute
    const id = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener('walkin-deals-change', refresh);
      window.removeEventListener('walkin-token-change', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', onStorage);
      clearInterval(id);
    };
  }, [refresh]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(id);
  }, [toast]);

  const isExpiredOrDeleted = (s) => s === STATUS.EXPIRED || s === STATUS.DELETED;
  const filtered = deals.filter((d) => {
    if (tab === TAB_KEYS.EXPIRED) return isExpiredOrDeleted(d.status);
    return d.status === tab;
  });

  const counts = {
    [TAB_KEYS.ACTIVE]:    deals.filter((d) => d.status === STATUS.ACTIVE).length,
    [TAB_KEYS.SCHEDULED]: deals.filter((d) => d.status === STATUS.SCHEDULED).length,
    [TAB_KEYS.PAUSED]:    deals.filter((d) => d.status === STATUS.PAUSED).length,
    [TAB_KEYS.EXPIRED]:   deals.filter((d) => isExpiredOrDeleted(d.status)).length,
  };

  const isSeed = (deal) => deal.area === '__seed__';

  const softDelete = async (deal) => {
    if (isDemoMode() || isSeed(deal)) {
      if (isSeed(deal)) {
        setDeals((prev) =>
          prev.map((d) =>
            d.id === deal.id
              ? { ...d, status: STATUS.DELETED, deletedAt: new Date().toISOString() }
              : d
          )
        );
      } else {
        updateDeal(deal.id, {
          status: STATUS.DELETED,
          deletedAt: new Date().toISOString(),
        });
      }
      setToast('Deal verwijderd — terug te vinden in Verlopen tab');
      setTab(TAB_KEYS.EXPIRED);
      return;
    }
    // Soft delete goes through DELETE /deals/:id, which sets `active: false`.
    // The old PATCH to status 'verwijderd' was rejected outright — deals.status
    // has no such value — so deleting always failed in live mode.
    try {
      await apiDeleteDeal(deal.id);
      setDeals((prev) => prev.filter((d) => d.id !== deal.id));
      setConfirmPermDelete(null);
      setToast('🗑️ Deal verwijderd');
      await refresh();
    } catch (e) {
      setToast(e?.message || 'Verwijderen mislukt.');
    }
  };

  const togglePause = async (deal) => {
    if (deal.status === STATUS.EXPIRED) return;
    const next = deal.status === STATUS.ACTIVE ? STATUS.PAUSED : STATUS.ACTIVE;
    if (isDemoMode() || isSeed(deal)) {
      if (isSeed(deal)) {
        setDeals((prev) => prev.map((d) => (d.id === deal.id ? { ...d, status: next } : d)));
      } else {
        updateDeal(deal.id, { status: next });
      }
      return;
    }
    try {
      await updateDealStatus(deal.id, next);
      await refresh();
    } catch (e) {
      setToast(e?.message || 'Bijwerken mislukt.');
    }
  };

  const permanentDelete = async (dealId) => {
    if (isDemoMode()) {
      let all = [];
      try { all = JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]'); }
      catch { all = []; }
      const remaining = all.filter((d) => d.id !== dealId);
      localStorage.setItem('walkin_shop_deals', JSON.stringify(remaining));
      window.dispatchEvent(new Event('walkin-deals-change'));
      setDeals((prev) => prev.filter((d) => d.id !== dealId));
      setConfirmPermDelete(null);
      setToast('🗑️ Deal definitief verwijderd');
      return;
    }
    try {
      await apiDeleteDeal(dealId);
      await refresh();
      setConfirmPermDelete(null);
      setToast('🗑️ Deal definitief verwijderd');
    } catch (e) {
      setToast(e?.message || 'Definitief verwijderen mislukt.');
    }
  };

  const reactivate = async (deal) => {
    const balance = getTokenBalance();
    if (!balance.unlimited && balance.available <= 0) {
      setModal('no-tokens');
      return;
    }

    if (isDemoMode() || isSeed(deal)) {
      const consumed = consumeToken();
      if (!consumed) { setModal('no-tokens'); return; }
      if (isSeed(deal)) {
        setDeals((prev) =>
          prev.map((d) =>
            d.id === deal.id
              ? { ...d, status: STATUS.ACTIVE, expiresAt: endOfTodayISO() }
              : d
          )
        );
      } else {
        updateDeal(deal.id, { status: STATUS.ACTIVE, expiresAt: endOfTodayISO() });
      }
      setToast(
        consumed.unlimited
          ? 'Deal heractiveerd (Unlimited).'
          : `Deal heractiveerd. Nog ${consumed.available} tokens.`
      );
      return;
    }

    // Live mode: reactivating has to push the expiry back out as well as flip
    // the status. A PATCH on the status alone left validUntil in the past, so
    // the deal came straight back as verlopen and the token was spent for
    // nothing. One token buys one day, same as demo mode. `active: true` undoes
    // the soft delete, and a validFrom still in the future is pulled to now so
    // the renewed deal is live immediately (and valid_until > valid_from holds).
    try {
      const startsLater =
        deal.validFrom && new Date(deal.validFrom).getTime() > Date.now();
      await apiUpdateDeal(deal.id, {
        status: STATUS.ACTIVE,
        active: true,
        expiresAt: endOfTodayISO(),
        ...(startsLater ? { validFrom: new Date().toISOString() } : {}),
      });
      // consumeTokens (plural) spends from the backend ledger; consumeToken
      // (singular) only moves the browser copy, so in live mode it left
      // tokens_used untouched and the spend was undone by the next balance
      // refresh. Ordering is unchanged and matches deal creation: the deal
      // is written first, then paid for.
      const consumed = await consumeTokens(1);
      await refresh();

      // The live branch had no null check — it rendered `consumed?.available
      // ?? 0`, so a refused spend read as "Nog 0 tokens", which looks like a
      // successful payment that emptied the wallet. The deal is already
      // reactivated by this point, so say exactly that instead.
      if (!consumed) {
        setToast('Deal heractiveerd, maar er was geen token meer om ervoor te betalen.');
        return;
      }

      setToast(
        consumed.unlimited
          ? 'Deal heractiveerd (Unlimited).'
          : `Deal heractiveerd. Nog ${consumed.available} tokens.`
      );
    } catch (e) {
      setToast(e?.message || 'Heractiveren mislukt.');
    }
  };

  return (
    <div className="shop-deals">
      <header className="shop-deals__header">
        <button
          className="shop-deals__back"
          onClick={() => navigate(-1)}
          aria-label="Terug"
        >
          ←
        </button>
        <h1 className="shop-deals__title">Mijn deals</h1>
        <span className="shop-deals__spacer" />
      </header>

      <div className="shop-deals__tabs">
        {[
          { key: TAB_KEYS.ACTIVE,    label: 'Actief'      },
          { key: TAB_KEYS.SCHEDULED, label: 'Gepland'     },
          { key: TAB_KEYS.PAUSED,    label: 'Gepauzeerd'  },
          { key: TAB_KEYS.EXPIRED,   label: 'Verlopen'    },
        ].map((t) => (
          <button
            key={t.key}
            className={`shop-deals__tab${tab === t.key ? ' shop-deals__tab--active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}{' '}
            <span className="shop-deals__tab-count">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      <main className="shop-deals__main">
        {filtered.length === 0 && (
          <div className="shop-deals__empty">
            {tab === TAB_KEYS.ACTIVE ? (
              <>
                <div className="shop-deals__empty-icon">🏷️</div>
                <p>Geen actieve deals.</p>
                <button
                  className="shop-deals__empty-btn"
                  onClick={() => navigate('/shop/new-deal')}
                >
                  ➕ Maak je eerste deal aan
                </button>
              </>
            ) : (
              <p>Geen deals in deze categorie.</p>
            )}
          </div>
        )}

        {filtered.map((deal) => {
          const isDel = deal.status === STATUS.DELETED;
          return (
          <div
            key={deal.id}
            className={`shop-deals__deal${isDel ? ' shop-deals__deal--deleted' : ''}`}
          >
            <div className="shop-deals__deal-head">
              <span className="shop-deals__deal-emoji">{deal.emoji || '🏷️'}</span>
              <div className="shop-deals__deal-text">
                <div className="shop-deals__deal-title">{deal.title}</div>
                <div className={`shop-deals__deal-badge shop-deals__deal-badge--${deal.status}`}>
                  {deal.status === STATUS.ACTIVE    && '🟢 Actief'}
                  {deal.status === STATUS.SCHEDULED && '📅 Gepland'}
                  {deal.status === STATUS.PAUSED    && '⏸️ Gepauzeerd'}
                  {deal.status === STATUS.EXPIRED   && '⏱️ Verlopen'}
                  {deal.status === STATUS.DELETED   && '🗑️ Verwijderd'}
                </div>
                {deal.area && deal.area !== '__seed__' && (
                  <div className="shop-deals__deal-area">📍 {deal.area}</div>
                )}
                {deal.status === STATUS.EXPIRED && deal.expiresAt && (
                  <div className="shop-deals__deal-when">
                    Verlopen op: {formatNlDate(deal.expiresAt)}
                  </div>
                )}
                {deal.status === STATUS.DELETED && deal.deletedAt && (
                  <div className="shop-deals__deal-when">
                    Verwijderd op: {formatNlDate(deal.deletedAt)}
                  </div>
                )}
                {deal.status === STATUS.SCHEDULED && deal.validFrom && (
                  <div className="shop-deals__deal-when shop-deals__deal-when--scheduled">
                    Wordt actief over {countdownText(deal.validFrom)} —{' '}
                    {formatNlDate(deal.validFrom)} {deal.startTime || ''}
                  </div>
                )}
              </div>
            </div>

            <div className="shop-deals__deal-meta">
              <span>{deal.views ?? deal.viewed ?? 0} bekeken</span>
              <span className="shop-deals__deal-dot">·</span>
              <span>{deal.redemptions ?? deal.redeemed ?? 0} ingeleverd</span>
              {deal.code && (
                <>
                  <span className="shop-deals__deal-dot">·</span>
                  <span className="shop-deals__deal-code">#{deal.code}</span>
                </>
              )}
            </div>

            <div className="shop-deals__deal-footer">
              <span className="shop-deals__deal-time">
                Geldig {deal.validFromTime || deal.validFrom || '--:--'} – {deal.validUntilTime || deal.validUntil || '--:--'}
              </span>
              <div className="shop-deals__deal-actions">
                {!isSeed(deal) && !isDel && (
                  <button
                    className="shop-deals__btn shop-deals__btn--edit"
                    onClick={() => navigate(`/shop/edit-deal/${deal.id}`)}
                    aria-label="Aanpassen"
                  >
                    ✏️ Aanpassen
                  </button>
                )}
                {deal.status === STATUS.ACTIVE && (
                  <button
                    className="shop-deals__btn shop-deals__btn--orange"
                    onClick={() => togglePause(deal)}
                  >
                    ⏸ Pauzeer
                  </button>
                )}
                {deal.status === STATUS.PAUSED && (
                  <button
                    className="shop-deals__btn shop-deals__btn--green"
                    onClick={() => togglePause(deal)}
                  >
                    ▶️ Activeer
                  </button>
                )}
                {(deal.status === STATUS.EXPIRED || deal.status === STATUS.DELETED) && (
                  <button
                    className="shop-deals__btn shop-deals__btn--reactivate"
                    onClick={() => reactivate(deal)}
                  >
                    🔄 Heractiveer — 1 token
                  </button>
                )}
                {!isSeed(deal) && !isDel && (
                  <button
                    className="shop-deals__btn shop-deals__btn--delete"
                    onClick={() => setConfirmPermDelete(deal)}
                    aria-label="Verwijder"
                  >
                    🗑️ Verwijder
                  </button>
                )}
              </div>
            </div>
          </div>
        );
        })}
      </main>

      <button
        className="shop-deals__fab"
        onClick={() => navigate('/shop/new-deal')}
        aria-label="Nieuwe deal"
      >
        ➕ Nieuwe deal
      </button>

      {toast && <div className="shop-deals__toast">{toast}</div>}

      {confirmPermDelete && (
        <div className="modal__overlay" role="dialog" onClick={() => setConfirmPermDelete(null)}>
          <div className="modal__card" onClick={(e) => e.stopPropagation()}>
            <div className="modal__icon">🗑️</div>
            <h3>Deal verwijderen?</h3>
            <p>
              "{confirmPermDelete.title}" verdwijnt direct uit de app en uit
              je overzicht. Dit kan niet ongedaan worden gemaakt.
            </p>
            <button
              className="modal__btn-danger"
              onClick={() => (isDemoMode() || isSeed(confirmPermDelete)
                ? permanentDelete(confirmPermDelete.id)
                : softDelete(confirmPermDelete))}
            >
              Verwijderen
            </button>
            <button
              className="modal__btn-cancel"
              onClick={() => setConfirmPermDelete(null)}
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {modal === 'no-tokens' && (
        <div className="shop-deals__modal" role="dialog">
          <div className="shop-deals__modal-card">
            <div className="shop-deals__modal-icon">🔒</div>
            <h3>Geen tokens beschikbaar</h3>
            <p>Heractiveren kost 1 token. Koop nieuwe tokens om door te gaan.</p>
            <button
              className="shop-deals__modal-buy"
              onClick={() => { setModal(null); navigate('/shop/tokens'); }}
            >
              Tokens kopen →
            </button>
            <button
              className="shop-deals__modal-cancel"
              onClick={() => setModal(null)}
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      <ShopBottomNav />
    </div>
  );
}
